import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Grid,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  LinearProgress,
} from '@mui/material';
import {
  TrendingUp,
  AttachMoney,
  AccountBalance,
  DirectionsCar,
  SailingOutlined,
  Star,
} from '@mui/icons-material';
import {
  hostEarningsService,
  HostDetailedEarnings,
  HostDetailedEarningsFilters,
} from '@/services/hostEarningsService';

interface Props {
  open: boolean;
  hostId: string | null;
  hostName: string;
  onClose: () => void;
}

const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-NG', {
    style: 'currency',
    currency: 'NGN',
    minimumFractionDigits: 0,
  }).format(amount);
};

const formatDate = (dateString: string): string => {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

export const HostDetailedEarningsModal: React.FC<Props> = ({
  open,
  hostId,
  hostName,
  onClose,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<HostDetailedEarnings | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [period, setPeriod] = useState<'all' | 'today' | 'week' | 'month' | 'year'>('all');

  useEffect(() => {
    if (open && hostId) {
      fetchDetailedEarnings();
    }
  }, [open, hostId, period]);

  const fetchDetailedEarnings = async () => {
    if (!hostId) return;

    try {
      setLoading(true);
      setError(null);
      const response = await hostEarningsService.getHostDetailedEarnings(hostId, { period });
      setData(response.data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load detailed earnings');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setData(null);
    setActiveTab(0);
    setPeriod('all');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Detailed Earnings: {hostName}</Typography>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Period</InputLabel>
            <Select value={period} onChange={(e: any) => setPeriod(e.target.value)} label="Period">
              <MenuItem value="all">All Time</MenuItem>
              <MenuItem value="today">Today</MenuItem>
              <MenuItem value="week">This Week</MenuItem>
              <MenuItem value="month">This Month</MenuItem>
              <MenuItem value="year">This Year</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </DialogTitle>

      <DialogContent>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : data ? (
          <Box>
            {/* Host Summary Cards */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ bgcolor: 'success.lighter', borderLeft: 4, borderColor: 'success.main' }}>
                  <CardContent>
                    <Typography variant="caption" color="text.secondary">
                      Total Earnings (Lifetime)
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: 'success.main' }}>
                      {formatCurrency(data.host.totalEarnings)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ bgcolor: 'primary.lighter', borderLeft: 4, borderColor: 'primary.main' }}>
                  <CardContent>
                    <Typography variant="caption" color="text.secondary">
                      Available Balance
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main' }}>
                      {formatCurrency(data.host.availableBalance)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ bgcolor: 'warning.lighter', borderLeft: 4, borderColor: 'warning.main' }}>
                  <CardContent>
                    <Typography variant="caption" color="text.secondary">
                      Total Withdrawn
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: 'warning.main' }}>
                      {formatCurrency(data.host.totalWithdrawn)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ bgcolor: 'info.lighter', borderLeft: 4, borderColor: 'info.main' }}>
                  <CardContent>
                    <Typography variant="caption" color="text.secondary">
                      Partner Tier
                    </Typography>
                    <Chip
                      label={data.host.partnerTier}
                      color="info"
                      size="small"
                      sx={{ mt: 1, textTransform: 'capitalize' }}
                    />
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Period Summary */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  {period === 'all' ? 'All Time' : period.charAt(0).toUpperCase() + period.slice(1)} Summary
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="caption" color="text.secondary">
                        Gross Earnings
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 700 }}>
                        {formatCurrency(data.summary.totalEarnings)}
                      </Typography>
                    </Box>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="caption" color="text.secondary">
                        Platform Fee ({data.summary.platformFeePercentage}%)
                      </Typography>
                      <Typography variant="body1" color="error.main">
                        - {formatCurrency(data.summary.platformFee)}
                      </Typography>
                    </Box>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="caption" color="text.secondary">
                        Commission VAT ({data.summary.commissionVatPercentage}%)
                      </Typography>
                      <Typography variant="body1" color="error.main">
                        - {formatCurrency(data.summary.commissionVat)}
                      </Typography>
                    </Box>
                    <Box sx={{ bgcolor: 'success.lighter', p: 2, borderRadius: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        Net Earnings
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 700, color: 'success.main' }}>
                        {formatCurrency(data.summary.netEarnings)}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="caption" color="text.secondary">
                        Completed Bookings
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 700 }}>
                        {data.summary.completedBookings}
                      </Typography>
                      {data.summary.pendingBookings > 0 && (
                        <Typography variant="caption" color="warning.main">
                          ({data.summary.pendingBookings} pending)
                        </Typography>
                      )}
                    </Box>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="caption" color="text.secondary">
                        Average Booking Value
                      </Typography>
                      <Typography variant="h6">
                        {formatCurrency(data.summary.avgBookingValue)}
                      </Typography>
                    </Box>
                    {data.summary.pendingEarnings > 0 && (
                      <Box sx={{ mb: 2, bgcolor: 'warning.lighter', p: 1.5, borderRadius: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                          Pending Earnings
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600, color: 'warning.main' }}>
                          {formatCurrency(data.summary.pendingEarnings)}
                        </Typography>
                      </Box>
                    )}
                    <Box sx={{ mb: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        Current Week: {formatCurrency(data.summary.currentWeekEarnings)}
                      </Typography>
                      {data.summary.weeklyGrowth !== 0 && (
                        <Chip
                          label={`${data.summary.weeklyGrowth > 0 ? '+' : ''}${data.summary.weeklyGrowth}%`}
                          size="small"
                          color={data.summary.weeklyGrowth > 0 ? 'success' : 'error'}
                          sx={{ ml: 1, height: 20 }}
                        />
                      )}
                    </Box>
                    <Box sx={{ mb: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        Current Month: {formatCurrency(data.summary.currentMonthEarnings)}
                      </Typography>
                      {data.summary.monthlyGrowth !== 0 && (
                        <Chip
                          label={`${data.summary.monthlyGrowth > 0 ? '+' : ''}${data.summary.monthlyGrowth}%`}
                          size="small"
                          color={data.summary.monthlyGrowth > 0 ? 'success' : 'error'}
                          sx={{ ml: 1, height: 20 }}
                        />
                      )}
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Current Year: {formatCurrency(data.summary.currentYearEarnings)}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* Tabs for detailed breakdown */}
            <Card>
              <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tab label="Vehicles" icon={<DirectionsCar />} iconPosition="start" />
                <Tab label="Yachts" icon={<SailingOutlined />} iconPosition="start" />
                <Tab label="Recent Transactions" icon={<AttachMoney />} iconPosition="start" />
              </Tabs>

              <Box sx={{ p: 2 }}>
                {/* Vehicles Tab */}
                {activeTab === 0 && (
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Vehicle</TableCell>
                          <TableCell align="right">Earnings</TableCell>
                          <TableCell align="center">Bookings</TableCell>
                          <TableCell align="center">Rating</TableCell>
                          <TableCell align="center">Utilization</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {data.earningsByVehicle.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={5} align="center">
                              <Typography variant="body2" color="text.secondary">
                                No vehicle earnings data
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ) : (
                          data.earningsByVehicle.map((vehicle) => (
                            <TableRow key={vehicle.vehicleId} hover>
                              <TableCell>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                  {vehicle.vehicleName}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {vehicle.plateNumber}
                                </Typography>
                              </TableCell>
                              <TableCell align="right" sx={{ fontWeight: 600, color: vehicle.totalEarnings > 0 ? 'success.main' : 'text.secondary' }}>
                                {formatCurrency(vehicle.totalEarnings)}
                                {vehicle.totalEarnings > 0 && (
                                  <Typography variant="caption" display="block" color="text.secondary">
                                    {formatCurrency(vehicle.averagePerBooking)}/booking
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell align="center">
                                <Typography variant="body2">{vehicle.completedBookings}</Typography>
                                {vehicle.activeBookings > 0 && (
                                  <Typography variant="caption" color="primary.main">
                                    ({vehicle.activeBookings} active)
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell align="center">
                                {vehicle.averageRating > 0 ? (
                                  <Box display="flex" alignItems="center" justifyContent="center" gap={0.5}>
                                    <Star fontSize="small" sx={{ color: 'warning.main' }} />
                                    <Typography variant="body2">{vehicle.averageRating.toFixed(1)}</Typography>
                                  </Box>
                                ) : (
                                  <Typography variant="caption" color="text.secondary">-</Typography>
                                )}
                              </TableCell>
                              <TableCell align="center">
                                <Box>
                                  <Typography variant="caption" sx={{ fontWeight: 600 }}>
                                    {vehicle.utilizationRate.toFixed(1)}%
                                  </Typography>
                                  <LinearProgress
                                    variant="determinate"
                                    value={vehicle.utilizationRate}
                                    sx={{ mt: 0.5, height: 6, borderRadius: 1 }}
                                    color={vehicle.utilizationRate > 50 ? 'success' : vehicle.utilizationRate > 20 ? 'warning' : 'error'}
                                  />
                                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                                    {vehicle.utilizationBreakdown.daysBooked}/{vehicle.utilizationBreakdown.daysListed} days
                                  </Typography>
                                </Box>
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}

                {/* Yachts Tab */}
                {activeTab === 1 && (
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Yacht</TableCell>
                          <TableCell align="right">Earnings</TableCell>
                          <TableCell align="center">Bookings</TableCell>
                          <TableCell align="center">Rating</TableCell>
                          <TableCell align="center">Utilization</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {!data.earningsByYacht || data.earningsByYacht.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={5} align="center">
                              <Typography variant="body2" color="text.secondary">
                                No yacht earnings data
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ) : (
                          data.earningsByYacht.map((yacht) => (
                            <TableRow key={yacht.yachtId} hover>
                              <TableCell>{yacht.yachtName}</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 600, color: 'success.main' }}>
                                {formatCurrency(yacht.totalEarnings)}
                              </TableCell>
                              <TableCell align="center">{yacht.completedBookings}</TableCell>
                              <TableCell align="center">
                                <Box display="flex" alignItems="center" justifyContent="center" gap={0.5}>
                                  <Star fontSize="small" sx={{ color: 'warning.main' }} />
                                  <Typography variant="body2">{yacht.averageRating.toFixed(1)}</Typography>
                                </Box>
                              </TableCell>
                              <TableCell align="center">
                                <Box>
                                  <Typography variant="caption">{yacht.utilizationRate}%</Typography>
                                  <LinearProgress
                                    variant="determinate"
                                    value={yacht.utilizationRate}
                                    sx={{ mt: 0.5, height: 6, borderRadius: 1 }}
                                  />
                                </Box>
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}

                {/* Recent Transactions Tab */}
                {activeTab === 2 && (
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Booking #</TableCell>
                          <TableCell>Vehicle/Yacht</TableCell>
                          <TableCell>Renter</TableCell>
                          <TableCell align="right">Amount</TableCell>
                          <TableCell>Duration</TableCell>
                          <TableCell>Completed</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {!data.recentTransactions || data.recentTransactions.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={6} align="center">
                              <Typography variant="body2" color="text.secondary">
                                No recent transactions
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ) : (
                          data.recentTransactions.map((transaction) => (
                            <TableRow key={transaction.bookingId} hover>
                              <TableCell>
                                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                  {transaction.bookingNumber}
                                </Typography>
                              </TableCell>
                              <TableCell>{transaction.vehicleName}</TableCell>
                              <TableCell>{transaction.renterName}</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 600, color: 'success.main' }}>
                                {formatCurrency(transaction.amount)}
                              </TableCell>
                              <TableCell>
                                <Typography variant="caption">
                                  {formatDate(transaction.startDate)} - {formatDate(transaction.endDate)}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="caption" color="text.secondary">
                                  {formatDate(transaction.completedAt)}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Box>
            </Card>
          </Box>
        ) : null}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};
